import { Vector } from "./Vector";
import { Time } from "./Time";
import { Sun } from "./Sun";
import { Earth } from "./Earth";
import { Conic } from "./Conic";
import * as Constants from "./Constants";
import * as Globals from "./Globals";

export class Mercury {

    /** Equatorial radius in km */
    static readonly RADIUS = 2439.7;

    /** Unicode symbol */
    static readonly SYMBOL: string = '\u263F';

    /** Standard magnitude at full phase and 1AU from both Earth and Sun */
    static readonly MAGNITUDE = -0.36;

    /** Get rough position vector in heliocentric ecliptic coordinates and units of km. */
    static getRoughPos(time: number): Vector {
        return Mercury.getOsculatingConic(time).getPos(time);
    }

    static getOsculatingConic(time: number): Conic {
        // First get time in Julian centuries since J2000
        let T = Time.getCenturiesSinceJ2000(time);

        // Compute osculating orbital elements
        let a = (0.38709893 + 0.00000066 * T) * Constants.AU;
        let e = 0.20563069 + 0.00002527 * T;
        let i = (7.00487 - 23.51 * T / 3600.0) * Constants.DEGTORAD;
        let node = (48.33167 - 446.3 * T / 3600.0) * Constants.DEGTORAD;
        let w = (77.45645 + 573.57 * T / 3600.0) * Constants.DEGTORAD - node;
        let L = (252.25084 + 415.20187368 * T * 360.0) * Constants.DEGTORAD;
        let M = Globals.reduceAngleTwoPi(L - w - node);

        // Set-up conic
        return Conic.fromKeplerElements(a, e, i, w, node, M, Sun.GM, time);
    }

    /** Gets a unit vector aligned with the north pole of the planet, in J2000 equatorial coordinates */
    static getPole(time: number): Vector {
        let T = Time.getCenturiesSinceJ2000(time);
        return Vector.fromAzElev((281.01 - 0.033 * T) * Constants.DEGTORAD, (61.45 - 0.005 * T) * Constants.DEGTORAD);
    }
}

export class Venus {

    /** Equatorial radius in km */
    static readonly RADIUS = 6051.8;

    /** Unicode symbol */
    static readonly SYMBOL: string = '\u2640';

    /** Standard magnitude at full phase and 1AU from both Earth and Sun */
    static readonly MAGNITUDE = -4.29;

    /** Get rough position vector in heliocentric ecliptic coordinates and units of km. */
    static getRoughPos(time: number): Vector {
        return Venus.getOsculatingConic(time).getPos(time);
    }

    static getOsculatingConic(time: number): Conic {
        // First get time in Julian centuries since J2000
        let T = Time.getCenturiesSinceJ2000(time);

        // Compute osculating orbital elements
        let a = (0.72333199 + 0.00000092 * T) * Constants.AU;
        let e = 0.00677323 - 0.00004938 * T;
        let i = (3.39471 - 2.86 * T / 3600.0) * Constants.DEGTORAD;
        let node = (76.68069 - 996.89 * T / 3600.0) * Constants.DEGTORAD;
        let w = (131.53298 - 108.8 * T / 3600.0) * Constants.DEGTORAD - node;
        let L = (181.97973 + 162.5494877 * T * 360.0) * Constants.DEGTORAD;
        let M = Globals.reduceAngleTwoPi(L - w - node);

        // Set-up conic
        return Conic.fromKeplerElements(a, e, i, w, node, M, Sun.GM, time);
    }

    /** Gets a unit vector aligned with the north pole of the planet, in J2000 equatorial coordinates */
    static getPole(time: number): Vector {
        return Vector.fromAzElev(272.72 * Constants.DEGTORAD, 67.15 * Constants.DEGTORAD);
    }
}

export class Mars {

    /** Equatorial radius in km */
    static readonly RADIUS = 3396.2;

    /** Unicode symbol */
    static readonly SYMBOL: string = '\u2642';

    /** Standard magnitude at full phase and 1AU from both Earth and Sun */
    static readonly MAGNITUDE = -1.52;

    /** Get rough position vector in heliocentric ecliptic coordinates and units of km. */
    static getRoughPos(time: number): Vector {
        return Mars.getOsculatingConic(time).getPos(time);
    }

    static getOsculatingConic(time: number): Conic {
        // First get time in Julian centuries since J2000
        let T = Time.getCenturiesSinceJ2000(time);

        // Compute osculating orbital elements
        let a = (1.52366231 - 0.00007221 * T) * Constants.AU;
        let e = 0.09341233 + 0.00011902 * T;
        let i = (1.85061 - 25.47 * T / 3600.0) * Constants.DEGTORAD;
        let node = (49.57854 - 1020.19 * T / 3600.0) * Constants.DEGTORAD;
        let w = (336.04084 + 1560.78 * T / 3600.0) * Constants.DEGTORAD - node;
        let L = (355.45332 + 53.167518348 * T * 360.0) * Constants.DEGTORAD;
        let M = Globals.reduceAngleTwoPi(L - w - node);

        // Set-up conic
        return Conic.fromKeplerElements(a, e, i, w, node, M, Sun.GM, time);
    }

    /** Gets a unit vector aligned with the north pole of the planet, in J2000 equatorial coordinates */
    static getPole(time: number): Vector {
        let T = Time.getCenturiesSinceJ2000(time);
        return Vector.fromAzElev((317.681 - 0.108 * T) * Constants.DEGTORAD, (52.886 - 0.061 * T) * Constants.DEGTORAD);
    }
}

export class Jupiter {

    /** Equatorial radius in km */
    static readonly RADIUS = 71492;

    /** Unicode symbol */
    static readonly SYMBOL: string = '\u2643';

    /** Standard magnitude at full phase and 1AU from both Earth and Sun */
    static readonly MAGNITUDE = -9.25;

    /** Get rough position vector in heliocentric ecliptic coordinates and units of km. */
    static getRoughPos(time: number): Vector {
        return Jupiter.getOsculatingConic(time).getPos(time);
    }

    static getOsculatingConic(time: number): Conic {
        // First get time in Julian centuries since J2000
        let T = Time.getCenturiesSinceJ2000(time);

        // Compute osculating orbital elements
        let a = (5.20336301 + 0.00060737 * T) * Constants.AU;
        let e = 0.04839266 - 0.0001288 * T;
        let i = (1.3053 - 4.15 * T / 3600.0) * Constants.DEGTORAD;
        let node = (100.55615 + 1217.17 * T / 3600.0) * Constants.DEGTORAD;
        let w = (14.75385 + 839.93 * T / 3600.0) * Constants.DEGTORAD - node;
        let L = (34.40438 + 8.429844405 * T * 360.0) * Constants.DEGTORAD;
        let M = Globals.reduceAngleTwoPi(L - w - node);

        // Set-up conic
        return Conic.fromKeplerElements(a, e, i, w, node, M, Sun.GM, time);
    }

    /** Gets a unit vector aligned with the north pole of the planet, in J2000 equatorial coordinates */
    static getPole(time: number): Vector {
        let T = Time.getCenturiesSinceJ2000(time);
        return Vector.fromAzElev((268.05 - 0.009 * T) * Constants.DEGTORAD, (64.49 + 0.003 * T) * Constants.DEGTORAD);
    }
}


// WEBPACK FOOTER //
// ./src/Planets.ts